/**
 * HubSpotNodeConfig.tsx
 * ──────────────────────
 * Structured configuration panel for the HubSpot CRM node.
 * Features:
 *  - Object type selector (Contacts, Deals, Companies, Tickets, Products, Line Items)
 *  - Operation picker (Fetch All / Search / Create / Update)
 *  - Integration connection selector (Private App token via IntegrationConnectionUI)
 *  - Dynamic property multi-select (fetched from /ops/hubspot/properties)
 *  - Filter builder with per-field operator options
 *  - Pagination controls
 *  - Write-mode warning
 */

import { useState, useEffect, useCallback } from 'react';
import {
    Users, Handshake, Building2, Ticket, Package, List,
    Search, Plus, Trash2, RefreshCw, ChevronDown, ChevronUp,
    AlertTriangle, Loader2, Check, X, Settings2
} from 'lucide-react';
import { toast } from 'sonner';
import { apiFetch, getStoredToken } from '@/api';
import { useAuth } from '@/context/useAuth';
import { IntegrationConnectionUI } from './IntegrationConnectionUI';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';

// ── Constants ─────────────────────────────────────────────────────────────────

const OBJECTS = [
    { value: 'contacts',   label: 'Contacts',   icon: Users,     color: '#ff7a59' },
    { value: 'deals',      label: 'Deals',      icon: Handshake, color: '#00bda5' },
    { value: 'companies',  label: 'Companies',  icon: Building2, color: '#f8c823' },
    { value: 'tickets',    label: 'Tickets',    icon: Ticket,    color: '#516f90' },
    { value: 'products',   label: 'Products',   icon: Package,   color: '#6a78d1' },
    { value: 'line_items', label: 'Line Items', icon: List,      color: '#e66e50' },
];

const OPERATIONS = [
    { value: 'fetch',  label: 'Fetch All',  desc: 'Get all records (paginated)' },
    { value: 'search', label: 'Search',     desc: 'Filter records with conditions' },
    { value: 'create', label: 'Create',     desc: 'Write: insert new records' },
    { value: 'update', label: 'Update',     desc: 'Write: patch existing records' },
];

// Operators per field type
const OPERATORS_BY_TYPE: Record<string, Array<{ value: string; label: string }>> = {
    string: [
        { value: 'EQ',           label: 'Equals'           },
        { value: 'NEQ',          label: 'Not equals'       },
        { value: 'CONTAINS_TOKEN', label: 'Contains'       },
        { value: 'NOT_CONTAINS_TOKEN', label: 'Not contains' },
        { value: 'HAS_PROPERTY', label: 'Has value'        },
        { value: 'NOT_HAS_PROPERTY', label: 'Is empty'     },
    ],
    number: [
        { value: 'EQ',  label: '= Equals'      },
        { value: 'NEQ', label: '≠ Not equals'  },
        { value: 'GT',  label: '> Greater than' },
        { value: 'GTE', label: '≥ ≥'           },
        { value: 'LT',  label: '< Less than'   },
        { value: 'LTE', label: '≤ ≤'           },
        { value: 'HAS_PROPERTY', label: 'Has value' },
        { value: 'NOT_HAS_PROPERTY', label: 'Is empty' },
    ],
    datetime: [
        { value: 'GT',           label: 'After'        },
        { value: 'LT',           label: 'Before'       },
        { value: 'GTE',          label: 'On or after'  },
        { value: 'LTE',          label: 'On or before' },
        { value: 'EQ',           label: 'On'           },
        { value: 'HAS_PROPERTY', label: 'Has value'    },
        { value: 'NOT_HAS_PROPERTY', label: 'Is empty' },
    ],
    bool: [
        { value: 'EQ',  label: 'Equals'     },
        { value: 'NEQ', label: 'Not equals' },
    ],
};

function getOperators(fieldType: string) {
    if (['number', 'currency'].includes(fieldType)) return OPERATORS_BY_TYPE.number;
    if (['date', 'datetime'].includes(fieldType)) return OPERATORS_BY_TYPE.datetime;
    if (fieldType === 'bool') return OPERATORS_BY_TYPE.bool;
    return OPERATORS_BY_TYPE.string;
}

// ── Types ──────────────────────────────────────────────────────────────────────

interface HubSpotProperty {
    name: string;
    label: string;
    type: string;
    fieldType: string;
    groupName?: string;
}

interface FilterRow {
    propertyName: string;
    operator: string;
    value: string;
}

interface FilterGroup {
    filters: FilterRow[];
}

interface HubSpotNodeConfigProps {
    node: any;
    onChangeData: (key: string | Record<string, any>, value?: any) => void;
}

// ── Component ─────────────────────────────────────────────────────────────────

export function HubSpotNodeConfig({ node, onChangeData }: HubSpotNodeConfigProps) {
    const { token } = useAuth();
    const data = node.data || {};

    const [object, setObject] = useState<string>(data.object || 'contacts');
    const [operation, setOperation] = useState<string>(data.operation || 'fetch');

    // Properties panel
    const [availableProps, setAvailableProps] = useState<HubSpotProperty[]>([]);
    const [loadingProps, setLoadingProps] = useState(false);
    const [propSearch, setPropSearch] = useState('');
    const [showPropPicker, setShowPropPicker] = useState(false);
    const [selectedProps, setSelectedProps] = useState<string[]>(data.properties || []);

    // Filters
    const [filterGroups, setFilterGroups] = useState<FilterGroup[]>(
        data.filterGroups || (data.filters ? [{ filters: data.filters }] : [{ filters: [] }])
    );

    // Pagination
    const [limit, setLimit] = useState<number>(data.limit || 100);
    const [maxPages, setMaxPages] = useState<number>(data.maxPages || 20);

    const isWriteOp = ['create', 'update', 'upsert'].includes(operation);

    // ── Fetch available properties when object or connectionId changes ──────
    const fetchProperties = useCallback(async () => {
        const tok = token || getStoredToken();
        if (!tok) return;
        setLoadingProps(true);
        try {
            const connId = data.connectionId || '';
            const result = await apiFetch(
                `/flows/ops/hubspot/properties?object=${object}&connection_id=${encodeURIComponent(connId)}`,
                tok
            );
            if (Array.isArray(result)) {
                setAvailableProps(result);
            }
        } catch (err: any) {
            // Silently fall back to defaults — the endpoint returns defaults when no connection
            console.warn('[HubSpotNodeConfig] Properties fetch failed:', err?.message);
        } finally {
            setLoadingProps(false);
        }
    }, [object, data.connectionId, token]);

    useEffect(() => { fetchProperties(); }, [fetchProperties]);

    // ── Sync local state → node data ──────────────────────────────────────────
    const commit = useCallback((patch: Record<string, any>) => {
        onChangeData(patch);
    }, [onChangeData]);

    const handleObjectChange = (val: string) => {
        setObject(val);
        setSelectedProps([]);
        setAvailableProps([]);
        commit({ object: val, properties: [], filters: [], filterGroups: [{ filters: [] }] });
    };

    const handleOperationChange = (val: string) => {
        setOperation(val);
        commit({ operation: val });
    };

    const toggleProp = (name: string) => {
        const next = selectedProps.includes(name)
            ? selectedProps.filter(p => p !== name)
            : [...selectedProps, name];
        setSelectedProps(next);
        commit({ properties: next });
    };

    const removeSelectedProp = (name: string) => {
        const next = selectedProps.filter(p => p !== name);
        setSelectedProps(next);
        commit({ properties: next });
    };

    // ── Filter builder helpers ────────────────────────────────────────────────
    const updateFilter = (groupIdx: number, filterIdx: number, patch: Partial<FilterRow>) => {
        const next = filterGroups.map((g, gi) =>
            gi !== groupIdx ? g : {
                ...g,
                filters: g.filters.map((f, fi) =>
                    fi !== filterIdx ? f : { ...f, ...patch }
                ),
            }
        );
        setFilterGroups(next);
        commit({
            filterGroups: next,
            filters: next[0]?.filters ?? [], // backward compat
        });
    };

    const addFilter = (groupIdx: number) => {
        const next = filterGroups.map((g, gi) =>
            gi !== groupIdx ? g : {
                ...g,
                filters: [...g.filters, { propertyName: '', operator: 'EQ', value: '' }],
            }
        );
        setFilterGroups(next);
        commit({ filterGroups: next });
    };

    const removeFilter = (groupIdx: number, filterIdx: number) => {
        const next = filterGroups.map((g, gi) =>
            gi !== groupIdx ? g : { ...g, filters: g.filters.filter((_, fi) => fi !== filterIdx) }
        );
        setFilterGroups(next);
        commit({ filterGroups: next, filters: next[0]?.filters ?? [] });
    };

    const addFilterGroup = () => {
        const next = [...filterGroups, { filters: [{ propertyName: '', operator: 'EQ', value: '' }] }];
        setFilterGroups(next);
        commit({ filterGroups: next });
    };

    const removeFilterGroup = (groupIdx: number) => {
        const next = filterGroups.filter((_, gi) => gi !== groupIdx);
        setFilterGroups(next.length ? next : [{ filters: [] }]);
        commit({ filterGroups: next });
    };

    // Helper: get property meta by name
    const getPropMeta = (name: string): HubSpotProperty | undefined =>
        availableProps.find(p => p.name === name);

    const filteredProps = availableProps.filter(
        p => !propSearch || p.label.toLowerCase().includes(propSearch.toLowerCase()) || p.name.toLowerCase().includes(propSearch.toLowerCase())
    );

    // ── Render ────────────────────────────────────────────────────────────────
    return (
        <div className="space-y-5">
            {/* ── Header ───────────────────────────────────────────────────── */}
            <div
                className="flex items-center gap-2.5 px-3 py-2.5 rounded-xl"
                style={{ background: 'linear-gradient(135deg, #ff7a59 0%, #ff5c35 100%)' }}
            >
                <div className="p-1.5 bg-white/20 rounded-lg">
                    <Settings2 className="h-4 w-4 text-white" />
                </div>
                <div>
                    <p className="text-white text-[10px] font-bold uppercase tracking-widest opacity-80">HubSpot CRM</p>
                    <p className="text-white text-xs font-bold">Smart Source Node</p>
                </div>
            </div>

            {/* ── Connection ───────────────────────────────────────────────── */}
            <div>
                <label className="text-[11px] font-semibold text-gray-600 uppercase tracking-wide block mb-1">
                    Connection
                </label>
                <IntegrationConnectionUI
                    protocol="HubSpot"
                    connectionId={data.connectionId || ''}
                    onChangeConnectionId={(val) => commit({ connectionId: val })}
                    newConnectionLabel={data._newConnLabel}
                    newConnectionFields={data._newConnFields}
                    onChangeNewLabel={(v) => commit({ _newConnLabel: v })}
                    onChangeNewFields={(v) => commit({ _newConnFields: v })}
                />
            </div>

            {/* ── Object Type ──────────────────────────────────────────────── */}
            <div className="space-y-2">
                <label className="text-[11px] font-semibold text-gray-600 uppercase tracking-wide">Object Type</label>
                <div className="grid grid-cols-3 gap-1.5">
                    {OBJECTS.map(obj => {
                        const Icon = obj.icon;
                        const active = object === obj.value;
                        return (
                            <button
                                key={obj.value}
                                type="button"
                                onClick={() => handleObjectChange(obj.value)}
                                className={`flex flex-col items-center gap-1 py-2 px-1 rounded-lg border text-[10px] font-semibold transition-all ${
                                    active
                                        ? 'bg-white border-2 shadow-sm'
                                        : 'bg-gray-50 border-gray-200 text-gray-500 hover:border-gray-300'
                                }`}
                                style={active ? { borderColor: obj.color, color: obj.color } : {}}
                            >
                                <Icon className="h-3.5 w-3.5" style={active ? { color: obj.color } : {}} />
                                {obj.label}
                            </button>
                        );
                    })}
                </div>
            </div>

            {/* ── Operation ────────────────────────────────────────────────── */}
            <div className="space-y-2">
                <label className="text-[11px] font-semibold text-gray-600 uppercase tracking-wide">Operation</label>
                <div className="grid grid-cols-2 gap-1.5">
                    {OPERATIONS.map(op => {
                        const active = operation === op.value;
                        return (
                            <button
                                key={op.value}
                                type="button"
                                onClick={() => handleOperationChange(op.value)}
                                className={`flex flex-col items-start px-3 py-2 rounded-lg border text-left transition-all ${
                                    active
                                        ? 'bg-indigo-50 border-indigo-400 shadow-sm'
                                        : 'bg-white border-gray-200 hover:border-indigo-200'
                                }`}
                            >
                                <span className={`text-[11px] font-bold ${active ? 'text-indigo-700' : 'text-gray-700'}`}>
                                    {op.label}
                                </span>
                                <span className="text-[9px] text-gray-400 mt-0.5">{op.desc}</span>
                            </button>
                        );
                    })}
                </div>

                {/* Write op warning */}
                {isWriteOp && (
                    <div className="flex items-start gap-2 p-2.5 rounded-lg bg-amber-50 border border-amber-200">
                        <AlertTriangle className="h-3.5 w-3.5 text-amber-500 flex-shrink-0 mt-0.5" />
                        <p className="text-[10px] text-amber-700 leading-tight">
                            Write operations are <strong>blocked in preview/test mode</strong>. They will only execute during a full scheduled run.
                        </p>
                    </div>
                )}
            </div>

            {/* ── Properties ───────────────────────────────────────────────── */}
            <div className="space-y-2">
                <div className="flex items-center justify-between">
                    <label className="text-[11px] font-semibold text-gray-600 uppercase tracking-wide">
                        Properties to Fetch
                    </label>
                    <button
                        type="button"
                        onClick={() => { setShowPropPicker(!showPropPicker); fetchProperties(); }}
                        className="flex items-center gap-1 text-[10px] text-indigo-600 hover:text-indigo-800 transition-colors"
                    >
                        <Plus className="h-3 w-3" />
                        Add fields
                    </button>
                </div>

                {/* Selected properties chips */}
                {selectedProps.length > 0 ? (
                    <div className="flex flex-wrap gap-1.5 p-2 rounded-lg border border-gray-100 bg-gray-50/50 min-h-[36px]">
                        {selectedProps.map(name => {
                            const meta = getPropMeta(name);
                            return (
                                <span
                                    key={name}
                                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-white border border-indigo-200 text-[10px] text-indigo-700 font-medium"
                                >
                                    {meta?.label || name}
                                    <button
                                        type="button"
                                        onClick={() => removeSelectedProp(name)}
                                        className="hover:text-red-500 transition-colors"
                                    >
                                        <X className="h-2.5 w-2.5" />
                                    </button>
                                </span>
                            );
                        })}
                    </div>
                ) : (
                    <p className="text-[10px] text-gray-400 italic">
                        No properties selected — defaults will be used.
                    </p>
                )}

                {/* Property picker dropdown */}
                {showPropPicker && (
                    <div className="rounded-xl border border-indigo-100 shadow-lg bg-white overflow-hidden">
                        <div className="p-2 border-b border-gray-100 flex items-center gap-2">
                            <Search className="h-3.5 w-3.5 text-gray-400 flex-shrink-0" />
                            <input
                                autoFocus
                                type="text"
                                placeholder="Search fields…"
                                value={propSearch}
                                onChange={(e) => setPropSearch(e.target.value)}
                                className="flex-1 text-xs outline-none bg-transparent placeholder-gray-400"
                            />
                            {loadingProps && <Loader2 className="h-3.5 w-3.5 animate-spin text-indigo-500 flex-shrink-0" />}
                            <button type="button" onClick={() => setShowPropPicker(false)}>
                                <X className="h-3.5 w-3.5 text-gray-400 hover:text-gray-700" />
                            </button>
                        </div>
                        <div className="max-h-56 overflow-y-auto divide-y divide-gray-50">
                            {filteredProps.length === 0 && (
                                <p className="px-3 py-4 text-[11px] text-gray-400 text-center italic">
                                    {loadingProps ? 'Loading…' : 'No properties found'}
                                </p>
                            )}
                            {filteredProps.map(p => {
                                const checked = selectedProps.includes(p.name);
                                return (
                                    <button
                                        key={p.name}
                                        type="button"
                                        onClick={() => toggleProp(p.name)}
                                        className="w-full flex items-center gap-2 px-3 py-1.5 hover:bg-indigo-50/50 transition-colors text-left"
                                    >
                                        <div className={`h-3.5 w-3.5 rounded border flex-shrink-0 flex items-center justify-center transition-colors ${
                                            checked ? 'bg-indigo-500 border-indigo-500' : 'border-gray-300'
                                        }`}>
                                            {checked && <Check className="h-2.5 w-2.5 text-white" />}
                                        </div>
                                        <div className="min-w-0 flex-1">
                                            <span className="text-[11px] text-gray-800 font-medium">{p.label}</span>
                                            <span className="text-[9px] text-gray-400 ml-2 font-mono">{p.name}</span>
                                        </div>
                                        <span className="text-[9px] text-gray-400 flex-shrink-0">{p.type || p.fieldType}</span>
                                    </button>
                                );
                            })}
                        </div>
                        <div className="p-2 border-t border-gray-100 flex items-center justify-between">
                            <span className="text-[10px] text-gray-400">{selectedProps.length} selected</span>
                            <button
                                type="button"
                                onClick={() => setShowPropPicker(false)}
                                className="text-[10px] text-indigo-600 font-semibold hover:underline"
                            >
                                Done
                            </button>
                        </div>
                    </div>
                )}
            </div>

            {/* ── Filter Builder (Search mode only) ─────────────────────────── */}
            {operation === 'search' && (
                <div className="space-y-2">
                    <div className="flex items-center justify-between">
                        <label className="text-[11px] font-semibold text-gray-600 uppercase tracking-wide">
                            Filters
                        </label>
                        <button
                            type="button"
                            onClick={addFilterGroup}
                            className="text-[10px] text-indigo-600 hover:text-indigo-800 flex items-center gap-1"
                        >
                            <Plus className="h-3 w-3" />
                            OR group
                        </button>
                    </div>

                    <div className="space-y-3">
                        {filterGroups.map((group, gi) => (
                            <div key={gi} className="rounded-xl border border-gray-200 overflow-hidden">
                                {/* Group header */}
                                <div className="flex items-center justify-between px-2.5 py-1.5 bg-gray-50 border-b border-gray-100">
                                    <span className="text-[10px] font-bold text-gray-500 uppercase tracking-wide">
                                        {gi === 0 ? 'WHERE' : 'OR WHERE'} — Match ALL (AND)
                                    </span>
                                    <div className="flex items-center gap-1.5">
                                        <button
                                            type="button"
                                            onClick={() => addFilter(gi)}
                                            className="text-[10px] text-indigo-600 flex items-center gap-0.5"
                                        >
                                            <Plus className="h-3 w-3" /> Add
                                        </button>
                                        {filterGroups.length > 1 && (
                                            <button
                                                type="button"
                                                onClick={() => removeFilterGroup(gi)}
                                                className="text-[10px] text-red-400"
                                            >
                                                <X className="h-3 w-3" />
                                            </button>
                                        )}
                                    </div>
                                </div>

                                {/* Filter rows */}
                                <div className="divide-y divide-gray-50">
                                    {group.filters.length === 0 && (
                                        <p className="px-3 py-3 text-[10px] text-gray-400 italic text-center">
                                            Click + Add to add a condition
                                        </p>
                                    )}
                                    {group.filters.map((filter, fi) => {
                                        const propMeta = getPropMeta(filter.propertyName);
                                        const ops = getOperators(propMeta?.type || 'string');
                                        const noValue = ['HAS_PROPERTY', 'NOT_HAS_PROPERTY'].includes(filter.operator);

                                        return (
                                            <div key={fi} className="grid grid-cols-[1fr_1fr_1fr_auto] gap-1.5 px-2 py-2 items-center">
                                                {/* Field */}
                                                <Select
                                                    value={filter.propertyName}
                                                    onValueChange={(v) => updateFilter(gi, fi, { propertyName: v, operator: 'EQ', value: '' })}
                                                >
                                                    <SelectTrigger className="h-7 text-[10px] px-2">
                                                        <SelectValue placeholder="Field…" />
                                                    </SelectTrigger>
                                                    <SelectContent className="bg-white z-[200] max-h-56 overflow-y-auto shadow-lg border border-gray-200">
                                                        {availableProps.map(p => (
                                                            <SelectItem key={p.name} value={p.name} className="text-xs">
                                                                {p.label}
                                                            </SelectItem>
                                                        ))}
                                                        <SelectItem value={filter.propertyName || '_custom'} className="text-xs italic text-gray-400">
                                                            {filter.propertyName || 'Type a name below…'}
                                                        </SelectItem>
                                                    </SelectContent>
                                                </Select>

                                                {/* Operator */}
                                                <Select
                                                    value={filter.operator}
                                                    onValueChange={(v) => updateFilter(gi, fi, { operator: v })}
                                                >
                                                    <SelectTrigger className="h-7 text-[10px] px-2">
                                                        <SelectValue />
                                                    </SelectTrigger>
                                                    <SelectContent className="bg-white z-[200] shadow-lg border border-gray-200">
                                                        {ops.map(o => (
                                                            <SelectItem key={o.value} value={o.value} className="text-xs">
                                                                {o.label}
                                                            </SelectItem>
                                                        ))}
                                                    </SelectContent>
                                                </Select>

                                                {/* Value */}
                                                {noValue ? (
                                                    <div className="h-7 px-2 text-[10px] text-gray-400 flex items-center italic bg-gray-50 rounded border border-gray-100">
                                                        no value needed
                                                    </div>
                                                ) : (
                                                    <Input
                                                        value={filter.value}
                                                        onChange={(e) => updateFilter(gi, fi, { value: e.target.value })}
                                                        className="h-7 text-[10px] px-2"
                                                        placeholder="value…"
                                                    />
                                                )}

                                                {/* Remove */}
                                                <button
                                                    type="button"
                                                    onClick={() => removeFilter(gi, fi)}
                                                    className="h-7 w-7 flex items-center justify-center rounded text-gray-400 hover:text-red-500 hover:bg-red-50 transition-colors"
                                                >
                                                    <Trash2 className="h-3 w-3" />
                                                </button>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* ── Pagination / Limits ───────────────────────────────────────── */}
            {!isWriteOp && (
                <div className="space-y-2">
                    <label className="text-[11px] font-semibold text-gray-600 uppercase tracking-wide">Limits</label>
                    <div className="grid grid-cols-2 gap-2">
                        <div className="space-y-1">
                            <label className="text-[10px] text-gray-500">Records per page</label>
                            <Input
                                type="number"
                                min={1} max={100}
                                value={limit}
                                onChange={(e) => {
                                    const v = Math.min(100, Math.max(1, parseInt(e.target.value) || 100));
                                    setLimit(v);
                                    commit({ limit: v });
                                }}
                                className="h-7 text-xs"
                            />
                            <p className="text-[9px] text-gray-400">Max 100 (HubSpot limit)</p>
                        </div>
                        <div className="space-y-1">
                            <label className="text-[10px] text-gray-500">Max pages</label>
                            <Input
                                type="number"
                                min={1} max={500}
                                value={maxPages}
                                onChange={(e) => {
                                    const v = Math.max(1, parseInt(e.target.value) || 20);
                                    setMaxPages(v);
                                    commit({ maxPages: v });
                                }}
                                className="h-7 text-xs"
                            />
                            <p className="text-[9px] text-gray-400">
                                ≈ {(limit * maxPages).toLocaleString()} max records
                            </p>
                        </div>
                    </div>
                </div>
            )}

            {/* ── Write mode: upsert key ────────────────────────────────────── */}
            {isWriteOp && (
                <div className="space-y-2">
                    <label className="text-[11px] font-semibold text-gray-600 uppercase tracking-wide">
                        Upsert Key Field
                    </label>
                    <Input
                        value={data.upsertKey || 'email'}
                        onChange={(e) => commit({ upsertKey: e.target.value })}
                        className="h-8 text-xs"
                        placeholder="e.g. email or hs_object_id"
                    />
                    <p className="text-[10px] text-gray-400">
                        Field used to match existing records for update/upsert.
                    </p>
                </div>
            )}
        </div>
    );
}

export default HubSpotNodeConfig;
